import { useEffect, useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from "chart.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMoneyBillWave,
  faClock,
  faCheckCircle,
  faDollarSign,
} from "@fortawesome/free-solid-svg-icons";
import { LoadingSkeleton, Select, StatCard } from "../../components/ui";
import { getPaymentsRevenueOvertime } from "./paymentsApi";
import { formatMoney, formatMonthLabel, MONTH_NAMES } from "./paymentsUtils";
import "./PlatformRevenueTab.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const CHART_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: "bottom",
    },
    tooltip: {
      callbacks: {
        label: (context) => `${context.dataset.label}: ${context.parsed.y} EGP`,
      },
    },
  },
  scales: {
    x: {
      grid: {
        display: false,
      },
    },
    y: {
      beginAtZero: true,
      grid: {
        color: "rgba(17, 24, 39, 0.08)",
      },
    },
  },
};

export default function PlatformRevenueTab() {
  const [months, setMonths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");

  useEffect(() => {
    let mounted = true;

    const fetchRevenue = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await getPaymentsRevenueOvertime();
        if (!mounted) return;
        setMonths(Array.isArray(data?.months) ? data.months : []);
      } catch (err) {
        if (mounted) {
          setError("Unable to load platform revenue.");
        }
        console.error("Error fetching platform revenue:", err);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchRevenue();
    return () => {
      mounted = false;
    };
  }, []);

  const currentMonth = useMemo(() => {
    const now = new Date();
    const match = months.find(
      (month) =>
        month.year === now.getFullYear() && month.month === now.getMonth() + 1,
    );
    return {
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      platformRevenue: match?.platformRevenue ?? 0,
      voucherDiscountTotal: match?.voucherDiscountTotal ?? 0,
      paidToDoctors: match?.paidToDoctors ?? 0,
      unpaidToDoctors: match?.unpaidToDoctors ?? 0,
    };
  }, [months]);

  const yearOptions = useMemo(
    () => [
      { value: "", label: "All years" },
      ...Array.from(new Set(months.map((month) => month.year)))
        .sort((a, b) => b - a)
        .map((year) => ({ value: String(year), label: String(year) })),
    ],
    [months],
  );

  const monthOptions = useMemo(
    () => [
      { value: "", label: "All months" },
      ...MONTH_NAMES.map((label, index) => ({
        value: String(index + 1),
        label,
      })),
    ],
    [],
  );

  const filteredMonths = useMemo(
    () =>
      months.filter(
        (month) =>
          (!selectedYear || month.year === Number(selectedYear)) &&
          (!selectedMonth || month.month === Number(selectedMonth)),
      ),
    [months, selectedYear, selectedMonth],
  );

  const chartData = useMemo(
    () => ({
      labels: filteredMonths.map(formatMonthLabel),
      datasets: [
        {
          label: "Platform Revenue",
          data: filteredMonths.map((month) =>
            Number(month.platformRevenue || 0),
          ),
          backgroundColor: "rgba(10, 127, 142, 0.75)",
          borderColor: "rgb(10, 127, 142)",
          borderWidth: 1,
        },
        {
          label: "Voucher Discounts",
          data: filteredMonths.map((month) =>
            Number(month.voucherDiscountTotal || 0),
          ),
          backgroundColor: "rgba(239, 68, 68, 0.65)",
          borderColor: "rgb(239, 68, 68)",
          borderWidth: 1,
        },
        {
          label: "Paid to Doctors",
          data: filteredMonths.map((month) => Number(month.paidToDoctors || 0)),
          backgroundColor: "rgba(16, 185, 129, 0.65)",
          borderColor: "rgb(16, 185, 129)",
          borderWidth: 1,
        },
        {
          label: "Unpaid to Doctors",
          data: filteredMonths.map((month) =>
            Number(month.unpaidToDoctors || 0),
          ),
          backgroundColor: "rgba(245, 158, 11, 0.65)",
          borderColor: "rgb(245, 158, 11)",
          borderWidth: 1,
        },
      ],
    }),
    [filteredMonths],
  );

  return (
    <section className="platform-revenue-panel">
      <div className="payments-revenue-summary">
        <div className="payments-revenue-summary-header">
          <h2 className="payments-revenue-title">Current month</h2>
          <p className="payments-revenue-subtitle">
            {formatMonthLabel(currentMonth)} · these cards reset to zero at the
            start of every month
          </p>
        </div>
        <StatCard
          title="Platform Revenue"
          value={formatMoney(currentMonth.platformRevenue)}
          icon={<FontAwesomeIcon icon={faMoneyBillWave} />}
          color="var(--primary-500)"
          loading={loading}
        />
        <StatCard
          title="Total Paid to Doctors"
          value={formatMoney(currentMonth.paidToDoctors)}
          icon={<FontAwesomeIcon icon={faCheckCircle} />}
          color="var(--success-500)"
          loading={loading}
        />
        <StatCard
          title="Total Unpaid"
          value={formatMoney(currentMonth.unpaidToDoctors)}
          icon={<FontAwesomeIcon icon={faClock} />}
          color="var(--warning-500)"
          loading={loading}
        />
        <StatCard
          title="Total Voucher Discounts"
          value={formatMoney(currentMonth.voucherDiscountTotal)}
          icon={<FontAwesomeIcon icon={faDollarSign} />}
          color="var(--danger-500)"
          loading={loading}
        />
      </div>

      {loading ? (
        <div className="payments-revenue-loading">
          <LoadingSkeleton width="100%" height="320px" />
          <LoadingSkeleton width="100%" height="220px" />
        </div>
      ) : error ? (
        <div className="payments-revenue-empty">
          <p>{error}</p>
        </div>
      ) : months.length === 0 ? (
        <div className="payments-revenue-empty">
          <p>No completed payment data found.</p>
        </div>
      ) : (
        <>
          <div className="payments-revenue-filters">
            <Select
              label="Year"
              containerClassName="payments-revenue-filter"
              value={selectedYear}
              onChange={(event) => setSelectedYear(event.target.value)}
              options={yearOptions}
            />
            <Select
              label="Month"
              containerClassName="payments-revenue-filter"
              value={selectedMonth}
              onChange={(event) => setSelectedMonth(event.target.value)}
              options={monthOptions}
            />
          </div>

          {filteredMonths.length === 0 ? (
            <div className="payments-revenue-empty">
              <p>No completed payment data for the selected month.</p>
            </div>
          ) : (
            <>
              <div className="payments-revenue-chart-card">
                <div className="payments-revenue-card-header">
                  <h2 className="payments-revenue-title">Monthly revenue</h2>
                  <p className="payments-revenue-subtitle">
                    Revenue, voucher discounts and doctor payouts by appointment
                    month
                  </p>
                </div>
                <div className="payments-revenue-chart">
                  <Bar data={chartData} options={CHART_OPTIONS} />
                </div>
              </div>

              <div className="payments-revenue-table-card">
                <div className="payments-revenue-card-header">
                  <h2 className="payments-revenue-title">Monthly breakdown</h2>
                  <p className="payments-revenue-subtitle">
                    Completed appointments grouped by scheduled month
                  </p>
                </div>
                <div className="payments-revenue-table-wrap">
                  <table className="payments-revenue-table">
                    <thead>
                      <tr>
                        <th>Month</th>
                        <th>Platform Revenue</th>
                        <th>Voucher Discounts</th>
                        <th>Paid to Doctors</th>
                        <th>Unpaid to Doctors</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredMonths.map((month) => (
                        <tr key={`${month.year}-${month.month}`}>
                          <td>{formatMonthLabel(month)}</td>
                          <td>{formatMoney(month.platformRevenue)}</td>
                          <td>{formatMoney(month.voucherDiscountTotal)}</td>
                          <td>{formatMoney(month.paidToDoctors)}</td>
                          <td>{formatMoney(month.unpaidToDoctors)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </section>
  );
}
